import { css } from '@emotion/core';
import { IThemeVariables } from './themeVariables';

const utilLayer = (themeVariables: IThemeVariables) => [
  css`
    z-index: ${themeVariables.misc.zIndex[0]};
  `,
  css`
    z-index: ${themeVariables.misc.zIndex[1]};
  `,
  css`
    z-index: ${themeVariables.misc.zIndex[2]};
  `,
  css`
    z-index: ${themeVariables.misc.zIndex[3]};
  `,
  css`
    z-index: ${themeVariables.misc.zIndex[4]};
  `,
  css`
    z-index: ${themeVariables.misc.zIndex[5]};
  `,
  css`
    z-index: ${themeVariables.misc.zIndex[6]};
  `,
  css`
    z-index: ${themeVariables.misc.zIndex[7]};
  `
];

const utilCursor = {
  colResize: css`
    cursor: col-resize;
  `,
  default: css`
    cursor: default;
  `,
  noPointer: css`
    pointer-events: none;
    user-select: none;
  `,
  notAllowed: css`
    cursor: not-allowed;
  `,
  pointer: css`
    cursor: pointer;
  `,
  select: css`
    cursor: text;
  `
};

const utilDisplay = {
  block: css`
    display: block;
  `,
  flex: css`
    display: flex;
  `,
  inline: css`
    display: inline;
  `,
  inlineBlock: css`
    display: inline-block;
  `,
  inlineFlex: css`
    display: inline-flex;
  `,
  none: css`
    display: none;
  `
};

const utilLineHeight = {
  double: css`
    line-height: 2;
  `,
  more: css`
    line-height: 1.75;
  `,
  none: css`
    line-height: 0;
  `,
  normal: css`
    line-height: 1.5;
  `,
  single: css`
    line-height: 1;
  `
};

const utilListStyle = {
  initial: css`
    list-style: initial;
  `,
  none: css`
    list-style: none;
  `
};

const utilOverflow = {
  auto: {
    all: css`
      overflow: auto;
    `,
    x: css`
      overflow-x: auto;
    `,
    y: css`
      overflow-y: auto;
    `
  },
  hidden: {
    all: css`
      overflow: hidden;
    `,
    x: css`
      overflow-y: hidden;
    `,
    y: css`
      overflow-x: hidden;
    `
  }
};

const utilPosition = {
  absolute: css`
    position: absolute;
  `,
  bottom: css`
    bottom: 0;
  `,
  fixed: css`
    position: fixed;
  `,
  left: css`
    left: 0;
  `,
  relative: css`
    position: relative;
  `,
  right: css`
    right: 0;
  `,
  static: css`
    position: static;
  `,
  top: css`
    top: 0;
  `
};

const utilShadow = [
  css`
    box-shadow: none !important;
  `,
  css`
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  `,
  css`
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23);
  `,
  css`
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23);
  `,
  css`
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22);
  `,
  css`
    box-shadow: 0 19px 38px rgba(0, 0, 0, 0.3), 0 15px 12px rgba(0, 0, 0, 0.22);
  `
];

const utilTextAlign = {
  center: css`
    text-align: center;
  `,
  justify: css`
    text-align: justify;
  `,
  left: css`
    text-align: left;
  `,
  right: css`
    text-align: right;
  `
};

const utilTextEllipsis = css`
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
`;

const utilTextTransform = {
  capitalize: css`
    text-transform: capitalize;
  `,
  lowercase: css`
    text-transform: lowercase;
  `,
  uppercase: css`
    text-transform: uppercase;
  `
};

const utilWhiteSpace = {
  nowrap: css`
    white-space: nowrap;
  `,
  wrap: css`
    white-space: wrap;
  `
};

const utilWidth = {
  full: css`
    width: 100%;
  `,
  half: css`
    width: 50%;
  `,
  none: css`
    width: 0;
  `,
  total: css`
    width: 100vw;
  `
};

const utilHeight = {
  full: css`
    height: 100%;
  `,
  half: css`
    height: 50%;
  `,
  none: css`
    height: 0;
  `,
  total: css`
    height: 100vh;
  `
};

const utilWordBreak = {
  all: css`
    word-break: break-all;
  `,
  word: css`
    word-break: break-word;
  `
};

const utilWordWrap = {
  all: css`
    word-wrap: break-all;
  `,
  word: css`
    word-wrap: break-word;
  `
};

const utilBorder = (themeVariables: IThemeVariables) => ({
  focus: css`
    border: 2px
      ${`${themeVariables.misc.borders.style.dashed} ${
        themeVariables.colors.element.dark
      }`};
  `,
  major: css`
    border: 2px
      ${`${themeVariables.misc.borders.style.default} ${
        themeVariables.colors.action.secondary.default
      }`};
  `,
  minor: css`
    border: 1px
      ${`${themeVariables.misc.borders.style.default} ${
        themeVariables.colors.misc.light
      }`};
  `,
  none: css`
    border: none;
  `,
  selected: css`
    border: 1px
      ${`${themeVariables.misc.borders.style.default} ${
        themeVariables.colors.misc.white
      }`};
  `,
  unselected: css`
    border: 1px
      ${`${themeVariables.misc.borders.style.default} ${
        themeVariables.colors.misc.transparent
      }`};
  `
});

const utilContent = {
  none: css`
    content: '';
  `
};

const utilOutline = (themeVariables: IThemeVariables) => ({
  focus: css`
    outline: 1px
      ${`${themeVariables.misc.borders.style.dashed} ${
        themeVariables.colors.element.dark
      }`};
  `,
  major: css`
    outline: 2px
      ${`${themeVariables.misc.borders.style.default} ${
        themeVariables.colors.action.secondary.default
      }`};
  `,
  minor: css`
    outline: 1px
      ${`${themeVariables.misc.borders.style.default} ${
        themeVariables.colors.misc.light
      }`};
  `,
  none: css`
    outline: none;
  `,
  selected: css`
    outline: 1px
      ${`${themeVariables.misc.borders.style.default} ${
        themeVariables.colors.misc.white
      }`};
  `,
  unselected: css`
    outline: 1px
      ${`${themeVariables.misc.borders.style.default} ${
        themeVariables.colors.misc.transparent
      }`};
  `
});

const utilBoxSizing = css`
  box-sizing: border-box;
`;

const utilShape = {
  circle: css`
    border-radius: 100%;
  `
};

export interface IUtilityVariables {
  border: object;
  box: object;
  content: object;
  cursor: object;
  display: object;
  height: object;
  layer: object;
  list: object;
  outline: object;
  overflow: object;
  position: object;
  shadow: object;
  shape: object;
  text: {
    align: object;
    ellipsis: object;
    lineheight: object;
    transform: object;
  };
  whitespace: object;
  width: object;
  word: {
    break: object;
    wrap: object;
  };
}

export const getUtilityVariables = (
  themeVariables: IThemeVariables
): IUtilityVariables => ({
  border: utilBorder(themeVariables),
  box: utilBoxSizing,
  content: utilContent,
  cursor: utilCursor,
  display: utilDisplay,
  height: utilHeight,
  layer: utilLayer(themeVariables),
  list: utilListStyle,
  outline: utilOutline(themeVariables),
  overflow: utilOverflow,
  position: utilPosition,
  shadow: utilShadow,
  shape: utilShape,
  text: {
    align: utilTextAlign,
    ellipsis: utilTextEllipsis,
    lineheight: utilLineHeight,
    transform: utilTextTransform
  },
  whitespace: utilWhiteSpace,
  width: utilWidth,
  word: {
    break: utilWordBreak,
    wrap: utilWordWrap
  }
});
